import {
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { mixpanelInit, mixpanelTrackPageView } from './asyncMixpanel';
import {
  identifyMixpanelUser,
  updateMixpanelUserAuthRole,
  updateMixpanelUserIsAcastEmail,
  updateMixpanelUserNetwork,
} from './mixpanelUser';
import { useAuth } from '@acast-tech/auth-client';
import { useDerivedNetworkContext } from '@/context';
import { useUserRoles } from '@/hooks/useUserRoles';
import { isProduction } from '@/config';
import { useDetectRageClicks } from './rageClicks';
import { reportWebVitals } from './webVitalsReporter';
import { useCookieConsent } from '@/hooks/useCookieConsent';
import { usePageMetadata } from '@/hooks';

/**
 * Initialize Mixpanel.
 *
 * The user is identified with a device ID, which is correlated with the user ID on login.
 *
 * See:
 * https://docs.mixpanel.com/docs/tracking/how-tos/identifying-users
 *
 */
export function MixpanelLoader({ projectToken }: { projectToken: string }): React.ReactNode {
  const { isImpersonating } = useAuth();
  const cookieConsent = useCookieConsent();
  const location = useLocation();
  const { page: pageType } = usePageMetadata();
  const { trackRageClicks } = useDetectRageClicks();

  useEffect(() => {
    if (!cookieConsent || isImpersonating) {
      return;
    }

    mixpanelInit(projectToken);
    trackRageClicks();
    reportWebVitals();

    if (!isProduction) {
      console.log('[Mixpanel] Mixpanel initialized');
      console.log('[WebVitals] Reporting enabled');
    }
  }, [projectToken, cookieConsent]);

  /*
   * Track page views manually as Mixpanel doesn't do this automatically in SPAs
   *
   * See: https://docs.mixpanel.com/docs/tracking/reference/javascript#tracking-page-views
   */
  useEffect(() => {
    if (!cookieConsent || isImpersonating) {
      return;
    }

    mixpanelTrackPageView(pageType);
  }, [location, pageType, cookieConsent, isImpersonating]);

  return null as unknown as React.ReactNode;
}

/**
 * Identify the authenticated user in Mixpanel and attach network/role properties.
 *
 * Fires once per session after the selected network and user roles have loaded.
 * Skipped entirely when impersonating to avoid polluting production analytics.
 *
 * See: https://docs.mixpanel.com/docs/tracking/how-tos/identifying-users
 */
export const MixpanelIdentifier = (): null => {
  const { isImpersonating, userId } = useAuth();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { data: userRolesData } = useUserRoles();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const { id: networkId, name: networkName } = selectedNetwork;

    if (!loaded && !isImpersonating && networkId && networkName && userRolesData) {
      setLoaded(true);

      identifyMixpanelUser(userId);
      // TODO: What do we do with multi-network users?
      // Possible with groups: https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#group-analytics
      updateMixpanelUserNetwork(networkId, networkName);

      const viewer = userRolesData?.viewer;

      if (viewer?.email.endsWith('@acast.com')) {
        updateMixpanelUserIsAcastEmail();
      }

      const authRole = viewer?.roles.find((r) =>
        r.scopes?.networkIds?.includes(networkId)
      );

      if (authRole) {
        updateMixpanelUserAuthRole(authRole.role.name);
      }
    }
  }, [
    loaded,
    userId,
    isImpersonating,
    selectedNetwork.id,
    selectedNetwork.name,
    userRolesData,
  ]);

  return null;
};
